import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useColumnPreferences } from '../../Hooks/useColumnPreferences';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  gridVisibleColumnFieldsSelector,
  gridFilteredSortedRowIdsSelector,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarContainer,
  GridColumnResizeParams,
  GridPanelWrapper,
  GridPanelContent,
  GridPanelFooter,
  GridSortModel,
} from '@mui/x-data-grid';
import { ArrowDownward, ArrowUpward, Check, Edit, ExpandMore, FileDownload } from '@mui/icons-material';
import { COLORS } from '../../Styles/Colors';
import { useCompactLayout } from '../../Hooks/useCompactLayout';
import { SearchableHeader } from '../Search/SearchableHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const HeaderWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  paddingTop: '12px',
});

const HeaderTitle = styled(Typography)({
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#64748b',
  letterSpacing: '0.06em',
  lineHeight: 1,
});

// Standard header component for non-searchable columns
export const StandardHeader: React.FC<{ title: string }> = ({ title }) => {
  return (
    <HeaderWrapper>
      <HeaderTitle>{title}</HeaderTitle>
    </HeaderWrapper>
  );
};

export interface ColumnDef<T = any> {
  field: keyof T;
  headerName: string;
  width?: number;
  minWidth?: number;
  sortable?: boolean;
  searchable?: boolean;
  renderHeader?: () => React.ReactNode;
  filterable?: boolean;
  renderCell?: (params: GridRenderCellParams) => React.ReactNode;
  valueGetter?: (value: any, row: any) => any;
  type?: 'string' | 'number' | 'date' | 'boolean';
  /** Overrides the type's default alignment (number columns default to right). */
  align?: 'left' | 'center' | 'right';
  defaultFirst?: boolean;
}

const TableSkeleton: React.FC<{ columns: ColumnDef<any>[], rows?: number }> = ({ columns, rows = 10 }) => {
  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, backgroundColor: '#f8f9fa', minHeight: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{ backgroundColor: 'white', borderRadius: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          padding: '16px', 
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={200} height={32} sx={{ borderRadius: 1 }} />
        </Box>

        <TableContainer sx={{ flex: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                {columns.map((column, index) => (
                  <TableCell 
                    key={index}
                    sx={{ 
                      borderBottom: '1px solid #f0f0f0',
                      width: column.width || 150,
                      minWidth: column.width || 150,
                    }}
                  >
                    <Skeleton 
                      variant="text" 
                      width="80%" 
                      height={20}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: rows }).map((_, rowIndex) => (
                <TableRow 
                  key={rowIndex}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f8f9fa' },
                    height: 52 
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell 
                      key={colIndex}
                      sx={{ 
                        borderBottom: '1px solid #f0f0f0',
                        width: column.width || 150,
                        minWidth: column.width || 150,
                      }}
                    >
                      <Skeleton 
                        variant={column.type === 'boolean' ? 'circular' : 'text'}
                        width={column.type === 'boolean' ? 20 : '70%'}
                        height={column.type === 'boolean' ? 20 : 16}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ 
          borderTop: '1px solid #f0f0f0',
          backgroundColor: 'white',
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 56
        }}>
          <Skeleton variant="text" width={150} height={20} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};


interface DataTableProps<T = any> {
  data: T[];
  columns: ColumnDef<T>[];
  title?: string;
  subtitle?: string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  idField?: keyof T | string;
  serverSidePagination?: boolean;
  page?: number;
  rowCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  children?: React.ReactNode;
  hideSearchBar?: boolean;
  hideColumnFilters?: boolean;
  titleExport?: string;
  pageKey?: string;
  defaultColumnVisibility?: Record<string, boolean>;
  serverSideSort?: boolean;
  onSortModelChange?: (field: string, order: 'asc' | 'desc' | null) => void;
  onMergeExport?: () => void;
  /** Menu label for `onMergeExport`. Defaults to "Merge & Export". */
  mergeExportLabel?: string;
  onEditAll?: () => void;
  editAllTooltip?: string;
  isEditAllMode?: boolean;
  isRowEditMode?: boolean;
  hasEdits?: boolean;
  editedCount?: number;
  onSaveChanges?: () => void;
  onCancelChanges?: () => void;
  /**
   * Opt-in master/detail. When `expandedRowId` matches a row, an extra full-width
   * row is inserted directly beneath it rendering `renderExpandedRow(row)`.
   * The detail row carries a copy of its parent's values so client-side sorting
   * and filtering keep it next to the row it belongs to.
   */
  renderExpandedRow?: (row: T) => React.ReactNode;
  expandedRowId?: string | number | null;
  /** Detail row height. Defaults to `'auto'` so the panel sizes to its content. */
  expandedRowHeight?: number | 'auto';
  /** Override default DataGrid row height (px). */
  rowHeight?: number;
  /**
   * Sort applied when the grid first mounts (header shows the arrow). With
   * `serverSideSort`, the page must request the same order from the API.
   */
  defaultSort?: GridSortModel;
}

const EXPANDED_ROW_PREFIX = '__expanded_row__';

const isExpandedRow = (row: any) => !!row?.__isExpandedRow;


const CustomExport = ({ titleExport = '', onMergeExport, mergeExportLabel = 'Merge & Export' }: { titleExport?: string; onMergeExport?: () => void; mergeExportLabel?: string }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const apiRef = useGridApiContext();
  const visibleFields = useGridSelector(apiRef, gridVisibleColumnFieldsSelector);
  const visibleRows = useGridSelector(apiRef, gridFilteredSortedRowIdsSelector);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportCsv = () => {
  const columns = visibleFields
    .filter(field => field !== 'actions' && field !== 'track' && field !== 'bins')
    .map((field) => {
      const colDef = apiRef.current.getColumn(field);
      return { field, headerName: colDef.headerName || field, type: colDef.type };
    });

  const headers = columns.map((col) => `"${col.headerName}"`);

  const rows = visibleRows.map((id) => {
    const row = apiRef.current.getRow(id);
    return columns.map((col) => {
      const value = apiRef.current.getCellValue(id, col.field) ?? row[col.field];
      if (value == null) return '"-"';

      if (value?.type === 'Buffer' && Array.isArray(value.data)) {
        return `"${value.data[0] === 1 ? 'Yes' : 'No'}"`;
      }

      switch (col.type) {
        case 'boolean':
          return `"${value ? 'Yes' : 'No'}"`;
        case 'date':
          const dateVal = value instanceof Date ? value : new Date(value);
          return `"${dateVal.toLocaleDateString()}"`;
        case 'number':
          return `"${typeof value === 'number' ? value.toLocaleString() : value}"`;
        default:
          return `"${String(value).replace(/"/g, '""')}"`; 
      }
    }).join(',');
  });

  const csvContent = [headers.join(','), ...rows].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'table_export.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  handleClose();
};


  const handleExportPdf = () => {
  const doc = new jsPDF('l', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const columns = visibleFields
    .filter(field => field !== 'actions' && field !== 'track' && field !== 'bins')
    .map((field) => {
      const colDef = apiRef.current.getColumn(field);
      return { field, headerName: colDef.headerName || field, type: colDef.type };
    });
  const headers = columns.map((col) => col.headerName);
  const rows = visibleRows.map((id) => {
    const row = apiRef.current.getRow(id);
    return columns.map((col) => {
      const value = apiRef.current.getCellValue(id, col.field) ?? row[col.field];
      if (value == null) return '-';
      if (value?.type === 'Buffer' && Array.isArray(value.data)) {
        return value.data[0] === 1 ? 'Yes' : 'No';
      }
      switch (col.type) {
        case 'boolean':
          return value ? 'Yes' : 'No';
        case 'date':
          return value instanceof Date
            ? value.toLocaleDateString()
            : new Date(value).toLocaleDateString();
        case 'number':
          return typeof value === 'number' ? value.toLocaleString() : value;
        default:
          return String(value);
      }
    });
  });

  doc.setFontSize(16);
  doc.text(titleExport, 14, 15);
  
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 22);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 28,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [32, 178, 170], 
      textColor: 255,
      fontStyle: 'bold',
    },
    margin: { left: 14, right: 14 },
  });

  doc.save('table_export.pdf');
  handleClose();
};

  const handlePrint = () => {
    window.print();
    handleClose();
  };

  return (
    <>
      <Tooltip title="Export">
        <Button
          size="small"
          startIcon={<FileDownload />}
          endIcon={<ExpandMore />}
          onClick={handleClick}
          sx={{
            fontSize: '0.875rem',
            textTransform: 'capitalize',
            color: COLORS.teal, 
            '&:hover': {
              backgroundColor: `${COLORS.teal}10`, 
            },
          }}
        >
          Export
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCsv}>
          Download as CSV
        </MenuItem>
        <MenuItem onClick={handleExportPdf}>
          Download as PDF
        </MenuItem>
        {onMergeExport && [
          <MenuItem key="divider" divider />,
          <MenuItem key="merge" onClick={() => { handleClose(); onMergeExport(); }}>
            {mergeExportLabel}
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};


const editToolbarSaveButtonSx = {
  backgroundColor: COLORS.teal,
  color: 'white',
  textTransform: 'capitalize',
  fontSize: '0.8125rem',
  fontWeight: 600,
  px: 1.5,
  py: 0.375,
  lineHeight: 1.5,
  '&:hover': { backgroundColor: '#00808f' },
};

const editToolbarCancelButtonSx = {
  textTransform: 'capitalize',
  fontSize: '0.875rem',
  color: COLORS.gray,
  fontWeight: 500,
  minHeight: 0,
  '&:hover': { backgroundColor: `${COLORS.gray}10` },
};

const editModeBadgeSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  backgroundColor: `${COLORS.teal}15`,
  border: `1px solid ${COLORS.teal}50`,
  borderRadius: 1,
  color: COLORS.teal,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
};

const toolbarActionButtonSx = {
  fontSize: '0.875rem',
  textTransform: 'capitalize',
  color: COLORS.teal,
  fontWeight: 500,
  minWidth: 0,
  '&:hover': { backgroundColor: `${COLORS.teal}10` },
};

const CustomGridToolbar = (props: any) => {
  const {
    showQuickFilter,
    quickFilterProps,
    titleExport,
    onMergeExport,
    mergeExportLabel,
    hideColumnFilters,
    pageKey,
    onSaveColumnDefaults,
    onResetColumnDefaults,
    isColumnPrefsDirty,
    columnPrefsSaveLabel = 'Save changes',
    columnPrefsJustSaved,
    onEditAll,
    editAllTooltip = 'Edit all editable rows on this page at once',
    isEditAllMode,
    isRowEditMode,
    hasEdits,
    editedCount,
    onSaveChanges,
    onCancelChanges,
  } = props;

  const isEditing = isEditAllMode || isRowEditMode || hasEdits;
  const showEditAllButton = !!onEditAll;
  const showEditToolbar = showEditAllButton || (onSaveChanges && onCancelChanges);
  const showLayoutSave = !!pageKey && !isEditing && (isColumnPrefsDirty || columnPrefsJustSaved);

  return (
    <GridToolbarContainer sx={{ flexWrap: 'wrap', gap: 0.5, p: 0, minWidth: 0 }}>
      {showQuickFilter && (
        <GridToolbarQuickFilter {...quickFilterProps} />
      )}
      {!hideColumnFilters && <GridToolbarFilterButton />}
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <CustomExport titleExport={titleExport} onMergeExport={onMergeExport} mergeExportLabel={mergeExportLabel} />

      {showLayoutSave && (
        <>
          <Box sx={{ width: '1px', height: 20, backgroundColor: `${COLORS.lightGray}55`, mx: 0.5, alignSelf: 'center' }} />
          {columnPrefsJustSaved ? (
            <Button size="small" sx={toolbarActionButtonSx} disabled>
              Saved
            </Button>
          ) : (
            <>
              <Tooltip title="Keep these column widths, order, and visibility next time you open this page">
                <Button
                  size="small"
                  onClick={onSaveColumnDefaults}
                  sx={toolbarActionButtonSx}
                >
                  {columnPrefsSaveLabel}
                </Button>
              </Tooltip>
              <Button
                size="small"
                onClick={onResetColumnDefaults}
                sx={editToolbarCancelButtonSx}
              >
                Reset
              </Button>
            </>
          )}
        </>
      )}

      {showEditToolbar && (
        <>
          <Box sx={{ width: '1px', height: 20, backgroundColor: '#e2e8f0', mx: 0.5, alignSelf: 'center' }} />

          {showEditAllButton && !isEditing ? (
            <Tooltip title={editAllTooltip}>
              <Button
                size="small"
                startIcon={<Edit sx={{ fontSize: 16 }} />}
                onClick={onEditAll}
                sx={{
                  fontSize: '0.875rem',
                  textTransform: 'capitalize',
                  color: COLORS.teal,
                  fontWeight: 500,
                  '&:hover': { backgroundColor: `${COLORS.teal}10` },
                }}
              >
                Edit All
              </Button>
            </Tooltip>
          ) : isEditing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isEditAllMode && (
                <Box sx={editModeBadgeSx}>
                  <Edit sx={{ fontSize: 12 }} />
                  Edit Mode
                </Box>
              )}

              {isRowEditMode && !isEditAllMode && (
                <Box sx={editModeBadgeSx}>
                  <Edit sx={{ fontSize: 12 }} />
                  Row Edit
                </Box>
              )}

              {hasEdits && (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  data-edit-toolbar-action
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={onSaveChanges}
                  sx={editToolbarSaveButtonSx}
                >
                  Save{editedCount ? ` (${editedCount})` : ''}
                </Button>
              )}

              <Button
                size="small"
                data-edit-toolbar-action
                onMouseDown={(e) => e.stopPropagation()}
                onClick={onCancelChanges}
                sx={editToolbarCancelButtonSx}
              >
                Cancel
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </GridToolbarContainer>
  );
};

const CustomColumnsPanel = (props: any) => {
  const allColDefs: ColumnDef<any>[] = props.allColumnDefs ?? [];
  const allFields = allColDefs.map((c) => String(c.field));

  // Merge saved order with current column defs:
  // - drop stale fields no longer in the column set
  // - append new columns (added after user last saved) at the end
  const savedOrder: string[] = props.orderedFields ?? [];
  const knownFields = new Set(allFields);
  const filteredSaved = savedOrder.filter((f) => knownFields.has(f));
  const extra = allFields.filter((f) => !savedOrder.includes(f));
  const displayOrder = filteredSaved.length > 0 ? [...filteredSaved, ...extra] : allFields;

  const colDefMap = Object.fromEntries(allColDefs.map((c) => [String(c.field), c]));
  const visibilityModel: Record<string, boolean> = props.visibilityModel ?? {};

  const moveUp = (index: number) => {
    if (index === 0) return;
    const next = [...displayOrder];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    props.onOrderChange?.(next);
  };

  const moveDown = (index: number) => {
    if (index === displayOrder.length - 1) return;
    const next = [...displayOrder];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    props.onOrderChange?.(next);
  };

  const handleToggle = (field: string, checked: boolean) => {
    props.onVisibilityChange?.({ ...visibilityModel, [field]: checked });
  };

  return (
    <GridPanelWrapper>
      <GridPanelContent sx={{ p: 0.5, width: '100%', boxSizing: 'border-box' }}>
        <Typography sx={{ fontSize: '0.75rem', color: COLORS.gray, px: 1, pt: 0.5, pb: 1, lineHeight: 1.4 }}>
          Show, hide, or reorder columns. Drag a column edge in the table to resize. Then click Save changes.
        </Typography>
        {displayOrder.map((field, index) => {
          const colDef = colDefMap[field];
          if (!colDef) return null;
          const headerName = colDef.headerName || (field === 'actions' ? 'Actions' : field);
          const isVisible = visibilityModel[field] !== false;
          return (
            <Box
              key={field}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 0.5,
                py: 0.25,
                px: 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: '#f1f5f9' },
              }}
            >
              <Checkbox
                size="small"
                checked={isVisible}
                onChange={(e) => handleToggle(field, e.target.checked)}
                sx={{ p: 0.5, color: COLORS.teal, '&.Mui-checked': { color: COLORS.teal } }}
              />
              <Typography sx={{ flex: 1, fontSize: '0.875rem', color: '#374151' }}>
                {headerName}
              </Typography>
              <IconButton
                size="small"
                onClick={() => moveUp(index)}
                disabled={index === 0}
                sx={{ p: 0.25, color: '#94a3b8', '&:not(.Mui-disabled):hover': { color: COLORS.teal } }}
              >
                <ArrowUpward sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => moveDown(index)}
                disabled={index === displayOrder.length - 1}
                sx={{ p: 0.25, color: '#94a3b8', '&:not(.Mui-disabled):hover': { color: COLORS.teal } }}
              >
                <ArrowDownward sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          );
        })}
      </GridPanelContent>

      {props.pageKey && (
        <GridPanelFooter
          sx={{
            gap: 1,
            justifyContent: 'flex-end',
            alignItems: 'center',
            p: 1.25,
            borderTop: '1px solid #e2e8f0',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={props.onReset}
            disabled={!props.hasSavedPreference && !props.isModelDirty}
            sx={{
              fontSize: '0.875rem',
              textTransform: 'capitalize',
              color: '#64748b',
              borderColor: '#64748b',
              '&:hover': { backgroundColor: '#64748b14', borderColor: '#64748b' },
              '&.Mui-disabled': { opacity: 0.4 },
            }}
          >
            Reset
          </Button>
        </GridPanelFooter>
      )}
    </GridPanelWrapper>
  );
};


const CustomToolbar = ({ 
  children, 
  ...toolbarProps 
}: { 
  children?: React.ReactNode;
  [key: string]: any;
}) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' },
      justifyContent: 'space-between', 
      alignItems: { xs: 'stretch', md: 'center' },
      gap: 1,
      padding: { xs: '8px 12px', md: '16px' },
      borderBottom: '1px solid #f0f0f0'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
        {children}
      </Box>
      
      <Box sx={{ minWidth: 0, overflowX: 'auto' }}>
        <CustomGridToolbar {...toolbarProps} />
      </Box>
    </Box>
  );
};



export const DataTable = <T extends Record<string, any>>({
  data = [],
  columns = [],
  loading = false,
  onRowClick,
  serverSidePagination = false,
  initialPageSize = 15,
  pageSizeOptions = [5, 10, 25, 50],
  idField = 'id',
  page = 0,
  rowCount = 0,
  onPageChange,
  onPageSizeChange,
  children,
  hideSearchBar = false,
  hideColumnFilters = false,
  titleExport,
  pageKey,
  defaultColumnVisibility = {},
  serverSideSort = false,
  onSortModelChange,
  onMergeExport,
  mergeExportLabel,
  onEditAll,
  editAllTooltip,
  isEditAllMode,
  isRowEditMode,
  hasEdits,
  editedCount,
  onSaveChanges,
  onCancelChanges,
  renderExpandedRow,
  expandedRowId = null,
  expandedRowHeight = 'auto',
  rowHeight: rowHeightProp,
  defaultSort,
}: DataTableProps<T>) => {
  const isEditing = isEditAllMode || isRowEditMode || hasEdits;
  const isCompact = useCompactLayout();
  const colPrefs = useColumnPreferences(pageKey, defaultColumnVisibility);
  const apiRef = useGridApiRef();
  const horizontalScrollRef = useRef(0);
  const lockHorizontalScrollRef = useRef(false);
  const wasLoadingRef = useRef(loading);
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const touchWidthsRef = useRef(colPrefs.touchWidths);
  touchWidthsRef.current = colPrefs.touchWidths;
  const showInitialSkeleton = loading && data.length === 0;

  const resolvedRowHeight = rowHeightProp ?? (isCompact ? 48 : 52);

  const getGridScrollElements = () => {
    const root = apiRef.current?.rootElementRef?.current as HTMLElement | null;
    if (!root) {
      return { scroller: null as HTMLElement | null, headers: null as HTMLElement | null };
    }
    return {
      scroller: root.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null,
      headers: root.querySelector('.MuiDataGrid-columnHeaders') as HTMLElement | null,
    };
  };

  const readHorizontalScroll = () => {
    const { scroller, headers } = getGridScrollElements();
    const fromApi = apiRef.current?.getScrollPosition?.()?.left;
    const left = Math.abs(
      scroller?.scrollLeft
      ?? (typeof fromApi === 'number' ? fromApi : 0)
      ?? headers?.scrollLeft
      ?? 0,
    );
    return left;
  };

  const writeHorizontalScroll = (left: number) => {
    if (!left) return;
    const { scroller, headers } = getGridScrollElements();
    if (scroller && scroller.scrollLeft !== left) scroller.scrollLeft = left;
    if (headers && headers.scrollLeft !== left) headers.scrollLeft = left;
    apiRef.current?.scroll?.({ left });
  };

  const unlockHorizontalScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lockAndRestoreHorizontalScroll = () => {
    lockHorizontalScrollRef.current = true;
    writeHorizontalScroll(horizontalScrollRef.current);
    requestAnimationFrame(() => {
      writeHorizontalScroll(horizontalScrollRef.current);
    });
    if (unlockHorizontalScrollTimerRef.current) {
      clearTimeout(unlockHorizontalScrollTimerRef.current);
    }
    unlockHorizontalScrollTimerRef.current = setTimeout(() => {
      writeHorizontalScroll(horizontalScrollRef.current);
      if (!loadingRef.current) {
        lockHorizontalScrollRef.current = false;
      }
    }, 400);
  };

  useEffect(() => {
    if (showInitialSkeleton) return undefined;
    let cancelled = false;
    let detach: (() => void) | undefined;
    let attempts = 0;

    const tryAttach = () => {
      if (cancelled) return;
      const root = apiRef.current?.rootElementRef?.current as HTMLElement | null;
      if (!root) {
        if (attempts < 30) {
          attempts += 1;
          requestAnimationFrame(tryAttach);
        }
        return;
      }

      const isColumnHeaderTarget = (target: EventTarget | null) =>
        target instanceof Element && !!target.closest('.MuiDataGrid-columnHeader');

      const beginHeaderSortLock = () => {
        const current = readHorizontalScroll();
        if (current > 0) {
          horizontalScrollRef.current = current;
        }
        lockHorizontalScrollRef.current = true;
        lockAndRestoreHorizontalScroll();
      };

      const onPointerDown = (event: Event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('.MuiDataGrid-columnSeparator')) {
          touchWidthsRef.current();
        }
        if (!isColumnHeaderTarget(event.target)) return;
        beginHeaderSortLock();
      };

      const onKeyDown = (event: Event) => {
        const key = (event as KeyboardEvent).key;
        if (key !== 'Enter' && key !== ' ') return;
        if (!isColumnHeaderTarget(event.target)) return;
        beginHeaderSortLock();
      };

      const onScroll = (event: Event) => {
        const el = event.target as HTMLElement | null;
        if (!(el instanceof HTMLElement)) return;
        if (lockHorizontalScrollRef.current) {
          const saved = horizontalScrollRef.current;
          if (saved > 0 && Math.abs(el.scrollLeft) < saved - 1) {
            el.scrollLeft = saved;
          }
          return;
        }
        if (
          el.classList.contains('MuiDataGrid-virtualScroller')
          || el.classList.contains('MuiDataGrid-columnHeaders')
        ) {
          horizontalScrollRef.current = Math.abs(el.scrollLeft);
        }
      };

      root.addEventListener('pointerdown', onPointerDown, true);
      root.addEventListener('keydown', onKeyDown, true);
      root.addEventListener('scroll', onScroll, true);

      detach = () => {
        root.removeEventListener('pointerdown', onPointerDown, true);
        root.removeEventListener('keydown', onKeyDown, true);
        root.removeEventListener('scroll', onScroll, true);
      };
    };

    tryAttach();
    return () => {
      cancelled = true;
      detach?.();
    };
  }, [showInitialSkeleton, apiRef]);

  useLayoutEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = loading;
    if (loading) {
      lockHorizontalScrollRef.current = true;
      return;
    }
    if (wasLoading) {
      writeHorizontalScroll(horizontalScrollRef.current);
      requestAnimationFrame(() => {
        writeHorizontalScroll(horizontalScrollRef.current);
        lockHorizontalScrollRef.current = false;
      });
    }
  }, [loading]);

  const [columnSearchState, setColumnSearchState] = useState<Record<string, string>>({});
  const [columnPrefsJustSaved, setColumnPrefsJustSaved] = useState(false);

  const snapshotLiveColumnWidths = () => {
    const widths: Record<string, number> = { ...colPrefs.columnWidths };
    const api = apiRef.current as any;
    const allColumns = api?.getAllColumns?.() as Array<{ field?: string; computedWidth?: number; width?: number }> | undefined;
    if (Array.isArray(allColumns)) {
      allColumns.forEach((col) => {
        if (!col?.field) return;
        const width = col.computedWidth ?? col.width;
        if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
          widths[col.field] = Math.round(width);
        }
      });
    }
    return widths;
  };

  const handleSaveColumnDefaults = () => {
    colPrefs.handleSave(snapshotLiveColumnWidths());
    setColumnPrefsJustSaved(true);
    window.setTimeout(() => setColumnPrefsJustSaved(false), 1500);
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const field = params?.colDef?.field;
    if (!field) return;
    colPrefs.onColumnWidthChange(field, params.width);
  };

  const handleInternalSearch = (field: string, value: string) => {
    setColumnSearchState(prev => ({ ...prev, [field]: value }));
  };

  const handleInternalClearSearch = (field: string) => {
    setColumnSearchState(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const gridColumns: GridColDef[] = useMemo(() => columns.map((column) => {
    const field = String(column.field);
    const resolvedWidth = colPrefs.columnWidths[field] ?? column.width ?? 150;
    return {
    field,
    headerName: column.headerName,
    width: resolvedWidth,
    ...(column.minWidth !== undefined ? { minWidth: column.minWidth } : {}),
    ...(column.align ? { align: column.align, headerAlign: column.align } : {}),
    sortable: column.sortable !== false,
    filterable: column.filterable !== false,
    ...(column.valueGetter ? { valueGetter: column.valueGetter } : {}),
    renderHeader: column.renderHeader ||
      (column.searchable && !serverSidePagination
        ? () => (
            <SearchableHeader
              field={String(column.field)}
              headerName={column.headerName}
              searchValue={columnSearchState[String(column.field)] ?? ''}
              onSearchChange={handleInternalSearch}
              onClearSearch={handleInternalClearSearch}
            />
          )
        : () => <StandardHeader title={column.headerName} />),
    renderCell:
      column.renderCell ||
      ((params: GridRenderCellParams) => {
        const value = params.value;
        if (value == null) return '-';
        switch (column.type) {
          case 'date':
            return new Date(value).toLocaleDateString();
          case 'boolean':
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                {value ? (
                  <Check sx={{ color: COLORS.teal, fontSize: 20 }} />
                ) : (
                  <h1 style={{ color: COLORS.teal, fontWeight: 'lighter' }}>x</h1>
                )}
              </Box>
            );
          case 'number':
            return typeof value === 'number' ? value.toLocaleString() : value;
          default:
            return String(value);
        }
      }),
    type:
      column.type === 'date'
        ? 'date'
        : column.type === 'number'
        ? 'number'
        : column.type === 'boolean'
        ? 'boolean'
        : 'string',
  };
  }), [columns, colPrefs.columnWidths, columnSearchState, serverSidePagination]);

  // Apply user-saved column order. Unknown fields in orderedFields are skipped;
  // columns not yet in orderedFields (newly added) fall to the end, unless
  // defaultFirst is set — those prepend before all saved columns.
  const orderedGridColumns = useMemo(() => {
    if (!pageKey || colPrefs.orderedFields.length === 0) return gridColumns;
    const orderMap = new Map(colPrefs.orderedFields.map((f, i) => [f, i]));
    return [...gridColumns].sort((a, b) => {
      const getFallback = (col: any) =>
        (col as any).defaultFirst ? -1 : gridColumns.length;
      const ai = orderMap.has(a.field) ? orderMap.get(a.field)! : getFallback(a);
      const bi = orderMap.has(b.field) ? orderMap.get(b.field)! : getFallback(b);
      return ai - bi;
    });
  }, [gridColumns, pageKey, colPrefs.orderedFields]);

  const firstVisibleField = orderedGridColumns.find(
    (column) => colPrefs.columnVisibilityModel[column.field] !== false,
  )?.field;

  const visibleColumnCount = orderedGridColumns.filter(
    (column) => colPrefs.columnVisibilityModel[column.field] !== false,
  ).length;

  const displayColumns: GridColDef[] = useMemo(() => {
    if (!renderExpandedRow) return orderedGridColumns;
    return orderedGridColumns.map((column) => {
      const isFirstVisible = column.field === firstVisibleField;
      return {
        ...column,
        colSpan: (_value: any, row: any) =>
          isExpandedRow(row) && isFirstVisible ? visibleColumnCount : 1,
        renderCell: (params: GridRenderCellParams) => {
          if (isExpandedRow(params.row)) {
            return isFirstVisible ? renderExpandedRow(params.row.__expandedParent) : null;
          }
          return column.renderCell ? column.renderCell(params) : null;
        },
      };
    });
  }, [orderedGridColumns, renderExpandedRow, firstVisibleField, visibleColumnCount]);

  if (showInitialSkeleton) {
    return <TableSkeleton columns={columns} rows={initialPageSize} />;
  }

  const filteredData = !serverSidePagination
    ? data.filter(row =>
        Object.entries(columnSearchState).every(([field, value]) => {
          if (!value.trim()) return true;
          const cellValue = String(row[field as keyof T] ?? '').toLowerCase();
          return cellValue.includes(value.toLowerCase());
        })
      )
    : data;

  const dataWithIds = filteredData.map((row, index) => ({
    id: row[idField as keyof T] ?? index,
    ...row,
  }));

  // Insert the detail row straight after its parent. It inherits the parent's
  // values so it keeps the same sort/filter position, and is re-identified by a
  // prefixed id so `getRowId` stays unique.
  const rows = (() => {
    if (!renderExpandedRow || expandedRowId == null) return dataWithIds;
    const parentIndex = dataWithIds.findIndex(
      (row) => String(row[idField as keyof T] ?? '') === String(expandedRowId),
    );
    if (parentIndex === -1) return dataWithIds;

    const parent = dataWithIds[parentIndex];
    const detailId = `${EXPANDED_ROW_PREFIX}${expandedRowId}`;
    const detailRow = {
      ...parent,
      id: detailId,
      [idField as keyof T]: detailId,
      __isExpandedRow: true,
      __expandedParent: parent,
    };

    return [
      ...dataWithIds.slice(0, parentIndex + 1),
      detailRow,
      ...dataWithIds.slice(parentIndex + 1),
    ];
  })();

  const handleRowClick = (params: any) => {
    if (isExpandedRow(params.row)) return;
    if (onRowClick) {
      onRowClick(params.row);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        backgroundColor: '#f1f5f9',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: isEditing ? `1px solid ${COLORS.teal}40` : '1px solid #e2e8f0',
          boxShadow: isEditing ? `0 0 0 3px ${COLORS.teal}15, 0 1px 3px rgba(0,0,0,0.06)` : '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
          transition: 'border 0.2s ease, box-shadow 0.2s ease',
          overflow: 'hidden',
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={displayColumns}
          onColumnWidthChange={handleColumnWidthChange}
          loading={loading}
          columnHeaderHeight={isCompact ? 52 : 68}
          rowHeight={resolvedRowHeight}
          getRowId={(row) => row[idField as keyof T]}
          getRowHeight={
            renderExpandedRow
              ? (params) => (isExpandedRow(params.model) ? expandedRowHeight : null)
              : undefined
          }
          getRowClassName={
            renderExpandedRow
              ? (params) => (isExpandedRow(params.row) ? 'datatable-expanded-row' : '')
              : undefined
          }
          paginationMode={serverSidePagination ? 'server' : 'client'}
          sortingMode={serverSideSort ? 'server' : 'client'}
          onSortModelChange={(model) => {
            lockAndRestoreHorizontalScroll();
            const active = document.activeElement;
            if (active instanceof HTMLElement) {
              active.blur();
            }
            if (onSortModelChange) {
              const first = model[0];
              if (first) {
                onSortModelChange(first.field, first.sort ?? null);
              } else {
                onSortModelChange('', null);
              }
            }
          }}
          rowCount={serverSidePagination ? rowCount : undefined}
          paginationModel={serverSidePagination ? { page, pageSize: initialPageSize } : undefined}
          onPaginationModelChange={serverSidePagination ? (model) => {
            onPageChange?.(model.page);
            onPageSizeChange?.(model.pageSize);
          } : undefined}
          initialState={{
            ...(!serverSidePagination
              ? {
                  pagination: {
                    paginationModel: {
                      pageSize: initialPageSize,
                    },
                  },
                }
              : {}),
            ...(defaultSort ? { sorting: { sortModel: defaultSort } } : {}),
          }}
          pageSizeOptions={pageSizeOptions}
          onRowClick={onRowClick ? handleRowClick : undefined}
          columnVisibilityModel={colPrefs.columnVisibilityModel}
          onColumnVisibilityModelChange={colPrefs.onColumnVisibilityModelChange}
          slots={{
            toolbar: (props) => <CustomToolbar {...props} children={children} titleExport={titleExport} onMergeExport={onMergeExport} mergeExportLabel={mergeExportLabel} hideColumnFilters={hideColumnFilters} pageKey={pageKey} onSaveColumnDefaults={pageKey ? handleSaveColumnDefaults : undefined} onResetColumnDefaults={colPrefs.handleReset} isColumnPrefsDirty={colPrefs.isModelDirty} columnPrefsJustSaved={columnPrefsJustSaved} onEditAll={onEditAll} editAllTooltip={editAllTooltip} isEditAllMode={isEditAllMode} isRowEditMode={isRowEditMode} hasEdits={hasEdits} editedCount={editedCount} onSaveChanges={onSaveChanges} onCancelChanges={onCancelChanges} />,
            columnsPanel: (props) => <CustomColumnsPanel {...props} />,
          }}
          disableColumnFilter={hideColumnFilters}
          slotProps={{
            toolbar: {
              showQuickFilter: !hideSearchBar,
              quickFilterProps: { debounceMs: 500 },
            } as any,
            panel: {
              sx: {
                '& .MuiDataGrid-paper': {
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: 'calc(100vw - 24px)',
                },
              },
            },
            columnsPanel: {
              pageKey,
              onSave: colPrefs.handleSave,
              onReset: colPrefs.handleReset,
              hasSavedPreference: colPrefs.hasSavedPreference,
              isModelDirty: colPrefs.isModelDirty,
              visibilityModel: colPrefs.columnVisibilityModel,
              onVisibilityChange: colPrefs.onColumnVisibilityModelChange,
              orderedFields: colPrefs.orderedFields,
              onOrderChange: colPrefs.onOrderChange,
              allColumnDefs: columns,
            } as any,
          }}
          sx={{
            border: 'none',
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '2px solid #e2e8f0',
            },
            '& .MuiDataGrid-columnHeader': {
              alignItems: 'flex-start',
              paddingLeft: { xs: '8px', md: '16px' },
              paddingRight: '8px',
              overflowAnchor: 'none',
              '&:focus, &:focus-within': { outline: 'none' },
            },
            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
              width: '100%',
              height: '100%',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: '#e2e8f0',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f1f5f9',
              fontSize: '0.875rem',
              color: '#374151',
              paddingLeft: { xs: '8px', md: '16px' },
              paddingRight: '8px',
              display: 'flex',
              alignItems: 'center',
              '&:focus, &:focus-within': { outline: 'none' },
            },
            '& .MuiDataGrid-row': {
              '&:hover': { backgroundColor: '#f8fafc' },
              '&:last-child .MuiDataGrid-cell': { borderBottom: 'none' },
              cursor: onRowClick ? 'pointer' : 'default',
            },
            // The detail row is a single full-width cell: strip the grid's cell
            // chrome so the panel controls its own padding, and let it grow with
            // its content when `expandedRowHeight` is 'auto'.
            '& .datatable-expanded-row': {
              backgroundColor: '#f8fafc',
              cursor: 'default',
              '&:hover': { backgroundColor: '#f8fafc' },
              '& .MuiDataGrid-cell': {
                padding: 0,
                display: 'block',
                height: 'auto',
                maxHeight: 'none',
                overflow: 'visible',
                borderBottom: `1px solid #e2e8f0`,
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              minHeight: 'auto',
              padding: { xs: '4px 4px', md: '4px 8px' },
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-root': {
              overflow: 'auto',
              width: { xs: '100%', md: 'auto' },
            },
            '& .MuiTablePagination-selectLabel': {
              display: { xs: 'none', sm: 'block' },
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
              flexWrap: 'wrap',
              '& .MuiButton-root:not(.MuiButton-contained)': {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                color: COLORS.teal,
                minWidth: 0,
              },
              '& .MuiDataGrid-toolbarQuickFilter': {
                width: { xs: '100%', sm: 'auto' },
                minWidth: 0,
              },
            },
            '& .MuiDataGrid-panelWrapper': {
              width: '100%',
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: 'rgba(255,255,255,0.85)',
            },
            '& .MuiDataGrid-virtualScroller': { margin: 0, padding: 0, overflowAnchor: 'none' },
            '& .MuiDataGrid-virtualScrollerContent': { minHeight: '100%' },
            '& .MuiDataGrid-virtualScrollerRenderZone': { margin: 0, padding: 0 },
            '& *:focus': { outline: 'none' },
          }}
          disableRowSelectionOnClick={!onRowClick}
        />
      </Paper>
    </Box>
  );
};